import { StaticImage } from "gatsby-plugin-image"
import { useState } from "react"
import {
  FaChartLine,
  FaGift,
  FaHandshake,
  FaRotate,
  FaStar,
  FaUsers,
} from "react-icons/fa6"
import Header from "../components/Header"
import Layout from "../components/Layout"
import { PasswordScreen } from "../components/PasswordScreen"
import { btn, btnPrimary } from "../ds/button.module.css"
import { WhiteArrowRight } from "../ds/Shapes"
import "../index.css"
import { Form, Input, PhoneNumberInput } from "../myne/Form/Form"
import { CLOSE_BUTTON_STYLE, Modal, MODAL_SIZES } from "../myne/Modal/modal"
import { createLead } from "../services/signupService"

const howItWorks = [
  {
    number: "01",
    title: "Get your end client on Premagic",
    description:
      "Create the account and event for your client, or share their details with our CS team and we'll set it up for you.",
  },
  {
    number: "02",
    title: "Small events, free or your fee",
    description:
      "For BANT-disqualified events, give Premagic to your client for free or charge your own fee. Default is AI photo distribution, with upgrade options available.",
  },
  {
    number: "03",
    title: "Large events, introduce and earn",
    description:
      "For BANT-qualified events, introduce your client to our team. We demo, close, and you get a 25% success fee on the deal.",
  },
]

const benefits = [
  {
    icon: <FaStar className="text-2xl text-[#7251F4]" />,
    title: "Your branding on attendee galleries",
    description:
      "Every photo delivered through Premagic carries your brand to every attendee.",
  },
  {
    icon: <FaChartLine className="text-2xl text-[#7251F4]" />,
    title: "Earn a 25% success fee",
    description:
      "On every successful deal closure for qualified events, you take home 25% of the deal value.",
  },
  {
    icon: <FaUsers className="text-2xl text-[#7251F4]" />,
    title: "Referral leads back to you",
    description:
      "Our exclusive modules attract attention, and those leads flow back to our partners.",
  },
  {
    icon: <FaGift className="text-2xl text-[#7251F4]" />,
    title: "Value addition to your clients",
    description:
      "Offer AI-powered photo distribution and marketing tools your clients won't find elsewhere.",
  },
  {
    icon: <FaRotate className="text-2xl text-[#7251F4]" />,
    title: "Stronger client retention",
    description:
      "Clients stick with partners who deliver category-defining tech. Premagic makes you that partner.",
  },
  {
    icon: <FaHandshake className="text-2xl text-[#7251F4]" />,
    title: "Annual TPP credits",
    description:
      "5,000 attendees and 25,000 photos per year, usable across up to 50 events annually.",
  },
]

const terms = [
  "Partner shall not promote or endorse similar products during the contract period.",
  "25% success fee applies to pay-per-event deals. For subscriptions, it's 25% in year one and 5% on yearly renewals.",
  "Success fee does not apply if the end client is already a Premagic customer.",
  "Pricing is set by Premagic's market-specific price calculator. Max standard discount is 10%. Larger discounts are handled case-by-case.",
  "Annual membership fee is $850, renewed yearly.",
  "TPP credits: 5,000 attendees/year and 25,000 photos/year, across up to 50 events/year.",
]

const faqs = [
  {
    question: "What is BANT?",
    answer:
      "Budget, Authority, Need, Time. These are the four criteria we use to qualify event leads. BANT-qualified events are those where the client has budget, decision-making authority, a clear need, and a defined timeline.",
  },
  {
    question: "How is the success fee paid?",
    answer:
      "On every successful deal closure you introduce, you're entitled to 25% of the deal value. For subscription deals, it's 25% in year one and 5% on annual renewals.",
  },
  {
    question: "What if the end client is already using Premagic?",
    answer:
      "The success fee does not apply if the client is an existing Premagic customer.",
  },
  {
    question: "Can I offer discounts to close deals?",
    answer:
      "Yes, up to 10% off the market price set by our price calculator. Anything larger is handled case-by-case so neither you nor Premagic loses the deal.",
  },
  {
    question: "What do I get with TPP credits?",
    answer:
      "5,000 attendees/year and 25,000 photos/year, usable across up to 50 events in a year.",
  },
]

export default function TrustedPartnerProgramPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [businessName, setBusinessName] = useState("")
  const [email, setEmail] = useState("")
  const [dialCode, setDialCode] = useState("+91")
  const [phone, setPhone] = useState("")
  const [referralCode, setReferralCode] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  function toggleModal() {
    setShowModal(!showModal)
    if (showModal) {
      setError(null)
      setSuccess(false)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const trimmedName = businessName.trim()
    const trimmedEmail = email.trim()
    const trimmedPhone = phone.replace(/\s/g, "")

    if (!trimmedName || !trimmedEmail || !trimmedPhone.match(/^\d{9,13}$/)) {
      setError("Please check the details you have provided")
      return
    }

    setIsLoading(true)
    createLead({
      company_name: trimmedName,
      name: trimmedName,
      email: trimmedEmail,
      phone: `${dialCode}${trimmedPhone}`,
      avg_events_per_day: "",
      referral_source: referralCode.trim() || "TPP",
      lead_profile: "EVENT_PLANNER",
    })
      .then(() => {
        setIsLoading(false)
        setSuccess(true)
      })
      .catch(data => {
        setError(data.error || "Something went wrong. Please try again.")
        setIsLoading(false)
      })
  }

  if (!isAuthenticated) {
    return <PasswordScreen onSuccess={() => setIsAuthenticated(true)} />
  }

  return (
    <Layout
      title="Premagic Trusted Partner Program (TPP)"
      description="Partner with Premagic to offer your end clients a better attendee experience and organic event promotion, while growing your own brand and revenue on every event you deliver."
      keywords={[
        "Premagic Trusted Partner Program",
        "event management partner program",
        "event tech reseller program",
        "event agency partnerships",
        "Premagic TPP",
      ]}
      noIndex
    >
      <div>
        <div style={{ backgroundColor: "#7251F4" }}>
          <Header isWhiteLogo />
        </div>

        {/* Hero */}
        <section className="md:py-28 relative bg-[#7251F4] overflow-hidden">
          <div className="bg-gradient-to-t from-[#7251F4] to-transparent h-64 w-full absolute bottom-0 right-0 z-10"></div>

          <div className="px-5 md:px-0 mx-auto md:w-[1080px] flex items-start pt-10 md:pt-0 md:items-center min-h-[calc(100dvh-80px)] md:min-h-[500px] relative">
            <div className="md:w-[640px] text-left relative z-20">
              <div className="inline-block bg-white/10 border border-white/20 text-white text-xs md:text-sm font-semibold px-3 py-1 rounded-full mb-5">
                Trusted Partner Program
              </div>
              <h1 className="text-3xl md:text-6xl font-bold mb-6 text-white !leading-[1.2]">
                Deliver better events.{" "}
                <span className="text-[#61F2C2]">Grow your agency.</span>
              </h1>
              <p className="text-white text-lg mb-5 md:w-[540px]">
                Premagic partners with world-class event management teams to
                give their end clients a better attendee experience, organic
                event promotion, and a new revenue stream on every event.
              </p>
              <button
                onClick={toggleModal}
                className={`${btn} ${btnPrimary} text-xs mt-5 mb-10 md:mb-0 relative w-max group z-10`}
              >
                <span className="mr-4 ml-2">Become a Partner</span>
                <WhiteArrowRight className="transition duration-300 ease-in-out group-hover:translate-x-2 mr-2" />
              </button>
            </div>
            <div className="w-11/12 md:w-6/12 absolute right-0 bottom-0 z-10 animate-[float_5s_ease-in-out_infinite]">
              <StaticImage
                src="../images/partners/puzzles.png"
                alt="Partnership"
              />
            </div>
            <div className="w-full h-full absolute bottom-0 right-0 top-0">
              <StaticImage
                src="../images/partners/grid.png"
                alt="Grid"
                class="h-full"
              />
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 md:py-28 bg-white">
          <div className="px-5 md:px-0 mx-auto md:w-[1080px]">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-5xl font-semibold text-[#7251F4]">
                How the partnership works
              </h2>
              <p className="text-sm md:text-md text-black w-full md:w-2/3 mx-auto mt-4">
                Bring us your clients. We handle the product, demos and
                closures. You earn on every deal and strengthen every client
                relationship.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {howItWorks.map(step => (
                <div
                  key={step.number}
                  className="bg-white p-8 rounded-2xl border border-[#cbbeff] shadow-sm"
                >
                  <div className="text-4xl font-bold text-[#7251F4] mb-3">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-black">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-700">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 md:py-28 bg-[#f7f4ff]">
          <div className="px-5 md:px-0 mx-auto md:w-[1080px]">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-5xl font-semibold text-[#7251F4]">
                What partners get
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((item, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl border border-[#cbbeff] shadow-sm"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2 text-black">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Membership + Terms */}
        <section className="py-20 md:py-28 bg-white">
          <div className="px-5 md:px-0 mx-auto md:w-[1080px] grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#7251F4] mb-4">
                Membership
              </h2>
              <p className="text-gray-700 mb-6">
                An annual membership fee of <strong>$850</strong> gets you into
                the program, with renewal each year.
              </p>
              <div className="bg-[#f7f4ff] border border-[#cbbeff] rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-2">
                  Included annually
                </h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• 5,000 attendees / year</li>
                  <li>• 25,000 photos / year</li>
                  <li>• Up to 50 events / year</li>
                </ul>
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#7251F4] mb-4">
                Terms
              </h2>
              <ul className="space-y-3">
                {terms.map((term, index) => (
                  <li
                    key={index}
                    className="text-sm text-gray-700 flex items-start"
                  >
                    <span className="text-[#7251F4] mr-2 mt-1">•</span>
                    <span>{term}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-[#f7f4ff]">
          <div className="px-5 md:px-0 mx-auto md:w-[800px]">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-5xl font-semibold text-[#7251F4]">
                Questions, answered
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-[#cbbeff] rounded-2xl p-6 bg-white"
                >
                  <h3 className="text-lg font-semibold text-black mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-sm md:text-base text-gray-700">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="bg-[#7251F4] py-20 relative z-4 mb-10 overflow-hidden">
          <div className="px-5 md:px-0 mx-auto md:w-[1080px] text-center pt-10 md:pt-0 relative z-10">
            <h2 className="text-3xl md:text-5xl font-semibold mb-4 md:mb-6 text-white">
              Ready to partner with Premagic?
            </h2>
            <p className="text-white mb-2">
              Join the Trusted Partner Program and turn every event you deliver
              into a growth channel.
            </p>
            <button
              onClick={toggleModal}
              className={`${btn} ${btnPrimary} text-xs mt-5 mb-10 md:mb-0 relative w-max group z-10`}
            >
              <span className="mr-4 ml-2">Apply to TPP</span>
            </button>
          </div>
          <div className="w-full h-full absolute bottom-0 right-0 top-0 opacity-70">
            <StaticImage
              src="../images/partners/round-mesh.png"
              alt="Mesh"
              class="h-full"
            />
          </div>
        </section>

        {/* Application Modal */}
        <Modal
          show={showModal}
          close={toggleModal}
          size={MODAL_SIZES.MD}
          showCloseButton
          closeButtonStyle={CLOSE_BUTTON_STYLE.PRIMARY}
        >
          {success ? (
            <div className="text-center py-6">
              <h2 className="text-2xl font-bold mb-3 text-[#7251F4]">
                Application received
              </h2>
              <p className="text-gray-700">
                Thanks for your interest in the Premagic Trusted Partner
                Program. Our team will reach out shortly with next steps.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold mb-1">
                Apply to the{" "}
                <span className="text-[#7251F4]">Trusted Partner Program</span>
              </h2>
              <p className="text-sm text-gray-600 mb-5">
                Tell us about your agency. We'll follow up with next steps.
              </p>
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-sm">
                  {error}
                </div>
              )}
              <Form onSubmit={handleSubmit}>
                <Input
                  label="Business Name"
                  name="business_name"
                  type="text"
                  placeholder="eg. Wellbeing Studios"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  required
                />
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="eg. david@workemail.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                <div>
                  <p className="text-sm font-medium mb-1">Phone Number</p>
                  <PhoneNumberInput
                    dialCode={dialCode}
                    value={phone}
                    onDialCodeChange={setDialCode}
                    onPhoneNumberChange={setPhone}
                    placeholder="eg. 9605 547 547"
                  />
                </div>
                <Input
                  label="Referral Code / Member ID"
                  name="referral_code"
                  type="text"
                  placeholder="eg. MAGIC10"
                  value={referralCode}
                  onChange={e => setReferralCode(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${btn} ${btnPrimary} text-xs mt-5 mb-2 relative w-max group disabled:opacity-60`}
                >
                  {isLoading ? "Submitting..." : "Apply Now"}
                </button>
              </Form>
            </>
          )}
        </Modal>
      </div>
    </Layout>
  )
}
